import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { Loader } from '../components/Loader';
import { ErrorView } from '../components/ErrorView';
import { PageHeader } from '../components/PageHeader';
import { ModeGrid } from '../components/ModeGrid';
import { haptic } from '../lib/telegram';
import { ensurePushSubscription, shouldOfferPush, isNativeApp } from '../lib/push';
import { isMiniApp } from '../lib/auth';
import { IconTile, TabGlyph } from '../components/AppIcons';
import { CONTEST, isContestActive } from '@app/shared';

type IconKind = Parameters<typeof IconTile>[0]['kind'];

export function Home() {
  const { data, loading, error, reload } = useFetch(() => api.me(), []);

  return (
    <>
      <PageHeader title="Тести ПДР" subtitle="Підготовка до іспиту" />
      <div className="space-y-4 p-4">
        {loading && <Loader label="Завантаження…" />}
        {error && <ErrorView error={error} onRetry={reload} />}

        {data && (
          <>
            <section className="card flex items-center gap-3">
              {data.user.photoUrl ? (
                <img
                  src={data.user.photoUrl}
                  alt=""
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <div className="grid h-12 w-12 place-items-center rounded-full bg-tg-bg text-tg-hint">
                  <TabGlyph kind="profile" className="h-6 w-6" />
                </div>
              )}
              <div className="flex-1">
                <div className="font-medium">Привіт, {data.user.firstName ?? 'водію'}! 👋</div>
                <div className="text-xs text-tg-hint">
                  Рівень {data.user.level} · {data.user.xp} XP · 🔥 {data.user.streakDays} дн.
                </div>
              </div>
            </section>

            <section className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Тестів" value={data.stats.totalAttempts} />
              <Stat label="Здано" value={`${data.stats.passRate}%`} />
              <Stat label="Точність" value={`${data.stats.accuracy}%`} />
            </section>

            <PushPrompt />

            {/* Конкурс «приведи друзів» — банер видно, поки конкурс активний (див. shared/contest.ts) */}
            {!isNativeApp() && isContestActive() && (
              <Link
                to="/referral"
                onClick={() => haptic.light()}
                className="card flex items-center gap-3 border border-yellow-500/50 bg-yellow-500/10 hover:bg-yellow-500/15"
              >
                <IconTile kind="gift" className="h-10 w-10" glyphClass="h-5 w-5" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium">🏆 Конкурс: {CONTEST.prizeTop}</div>
                  <div className="text-xs text-tg-hint">
                    Приведи найбільше друзів до {CONTEST.endLabel} + розіграш Premium серед усіх.
                    Правила і твій рахунок →
                  </div>
                </div>
              </Link>
            )}

            <section className="space-y-2">
              <h2 className="px-1 text-sm font-semibold uppercase tracking-wide text-tg-hint">
                Обрати тест
              </h2>
              <ModeGrid
                isPremium={data.user.isPremium}
                mistakes={data.stats.mistakesCount}
                bookmarks={data.stats.bookmarksCount}
              />
            </section>

            <section className="space-y-2">
              {/* Битви й реферал — соц-фічі через Telegram; у нативній апці ховаємо */}
              {!isNativeApp() && (
                <>
                  <a
                    href="https://play.google.com/store/apps/details?id=online.pdrtest.app.twa"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => haptic.light()}
                    className="card flex items-center gap-3 hover:bg-tg-bg/40"
                  >
                    <IconTile kind="flash" className="h-10 w-10" glyphClass="h-5 w-5" />
                    <div className="flex-1">
                      <div className="font-medium">Завантаж наш додаток 📲</div>
                      <div className="text-xs text-tg-hint">
                        Google Play · тести працюють без інтернету
                      </div>
                    </div>
                    <span className="text-tg-hint">›</span>
                  </a>
                  <StartLink
                    to="/battles/new"
                    title="Битва з другом"
                    subtitle="Створи матч 1v1 і поділись посиланням"
                    icon="battle"
                  />
                  <StartLink
                    to="/referral"
                    title="Запроси друзів — отримай Premium"
                    subtitle="Запроси 3 друзів → +3 дні Premium безкоштовно"
                    icon="gift"
                  />
                </>
              )}
              <StartLink
                to="/leaderboard"
                title="Лідерборд"
                subtitle="Топ гравців за XP — побач свій ранг"
                icon="trophy"
              />
            </section>
          </>
        )}

        <footer className="border-t border-tg-secondary-bg/60 pt-3 text-center text-[11px] leading-relaxed text-tg-hint">
          Неофіційний навчальний застосунок. Не пов'язаний із МВС чи сервісними
          центрами (ТСЦ) і не представляє державні установи. Питання — за
          офіційними ПДР України. Офіційне джерело:{' '}
          <a
            href="https://hsc.gov.ua"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            hsc.gov.ua
          </a>
        </footer>
      </div>
    </>
  );
}

// Пропозиція ввімкнути сповіщення. Показується ОДИН раз і тільки у веб/Android-версії:
//   - у Telegram Mini App не показуємо (там сповіщення шле бот);
//   - якщо юзер уже дозволив або заблокував — не показуємо;
//   - хрестик = «більше не пропонувати» (localStorage).
const PUSH_DISMISSED_KEY = 'pushPromptDismissed';

function PushPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // shouldOfferPush асинхронний (у нативній апці питає стан дозволу через міст)
  useEffect(() => {
    if (isMiniApp() || localStorage.getItem(PUSH_DISMISSED_KEY)) return;
    let cancelled = false;
    void shouldOfferPush().then((offer) => {
      if (!cancelled && offer) setVisible(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible) return null;

  const enable = async () => {
    haptic.light();
    setBusy(true);
    setErr(null);
    const r = await ensurePushSubscription();
    setBusy(false);
    if (r.ok) {
      setVisible(false); // дозвіл збережено браузером — картка більше не потрібна
    } else {
      setErr(r.reason ?? 'Не вдалося');
    }
  };

  const dismiss = () => {
    haptic.light();
    localStorage.setItem(PUSH_DISMISSED_KEY, '1');
    setVisible(false);
  };

  return (
    <section className="card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <IconTile kind="bell" className="h-9 w-9 rounded-lg" glyphClass="h-5 w-5" />
          <div className="min-w-0">
            <div className="font-medium">Увімкнути сповіщення?</div>
            <div className="text-xs text-tg-hint">Нагадування про навчання та Premium. Питаємо один раз.</div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={enable}
            disabled={busy}
            className="rounded-lg bg-green-500 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? '…' : 'Увімкнути'}
          </button>
          <button
            onClick={dismiss}
            aria-label="Більше не пропонувати"
            className="rounded-lg px-2 py-2 text-tg-hint"
          >
            ✕
          </button>
        </div>
      </div>
      {err && <div className="mt-2 break-words text-xs text-tg-destructive">{err}</div>}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="card">
      <div className="text-xl font-semibold">{value}</div>
      <div className="text-xs text-tg-hint">{label}</div>
    </div>
  );
}

function StartLink({
  to,
  title,
  subtitle,
  icon,
}: {
  to: string;
  title: string;
  subtitle: string;
  icon: IconKind;
}) {
  return (
    <Link
      to={to}
      onClick={() => haptic.light()}
      className="card flex items-center gap-3 hover:bg-tg-bg/40"
    >
      <IconTile kind={icon} className="h-10 w-10" glyphClass="h-5 w-5" />
      <div className="flex-1">
        <div className="font-medium">{title}</div>
        <div className="text-xs text-tg-hint">{subtitle}</div>
      </div>
      <span className="text-tg-hint">›</span>
    </Link>
  );
}
